import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ArrowUpRight } from "lucide-react";

function formatDate(d) {
  if (!d) return "";
  try { return new Date(d).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" }); }
  catch { return ""; }
}

export default function NewspaperSection() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/substack/posts").then((r) => setPosts(r.data.posts || []))
      .catch(() => {}).finally(() => setLoading(false));
  }, []);

  const [lead, ...rest] = posts;
  const sub = rest.slice(0, 5);

  return (
    <section className="newsprint-bg py-20 px-6 md:px-12 lg:px-16 border-y-4 border-halo-navy" data-testid="newspaper-section">
      <div className="max-w-7xl mx-auto">
        <div className="border-b-4 border-double border-halo-navy pb-6 mb-10 flex items-end justify-between flex-wrap gap-3">
          <div>
            <div className="font-sports uppercase tracking-[0.3em] text-xs text-halo-red">From the Newsroom</div>
            <h2 className="font-editorial text-5xl sm:text-6xl lg:text-7xl text-halo-navy mt-1 italic">
              The Front Page
            </h2>
          </div>
          <div className="font-editorial italic text-halo-navy/60 text-sm">
            Vol. 1 · {new Date().toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })}
          </div>
        </div>

        {loading ? (
          <div className="grid md:grid-cols-3 gap-8">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="h-64 bg-halo-navy/5 animate-pulse" />
            ))}
          </div>
        ) : posts.length === 0 ? (
          <div className="text-center py-16 border-2 border-dashed border-halo-navy/30">
            <p className="font-editorial italic text-xl text-halo-navy/60 mb-2">The presses are warming up.</p>
            <p className="font-body text-sm text-halo-navy/60">
              Substack posts will appear here automatically. Subscribe below to be notified.
            </p>
            <a
              data-testid="substack-subscribe-link"
              href="https://substack.com/@angelsbaseballhiti"
              target="_blank" rel="noreferrer"
              className="inline-flex items-center gap-2 mt-6 bg-halo-navy text-halo-cream px-5 py-3 font-sports uppercase tracking-wider text-xs"
            >
              Subscribe on Substack <ArrowUpRight className="w-4 h-4" strokeWidth={2.5} />
            </a>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-10">
            {lead && (
              <article className="lg:col-span-7 border-b-2 lg:border-b-0 lg:border-r-2 border-halo-navy/30 pb-10 lg:pr-10 lg:pb-0" data-testid="news-lead-article">
                {lead.image && (
                  <div className="aspect-[16/10] overflow-hidden mb-5 border-b-4 border-halo-navy">
                    <img src={lead.image} alt={lead.title} className="w-full h-full object-cover" loading="lazy" />
                  </div>
                )}
                <div className="font-sports uppercase tracking-[0.25em] text-[10px] text-halo-red mb-3">Lead Story</div>
                <h3 className="font-editorial text-3xl sm:text-4xl lg:text-5xl text-halo-navy leading-[1.05] mb-4">
                  <a href={lead.url} target="_blank" rel="noreferrer" className="hover:text-halo-red">{lead.title}</a>
                </h3>
                <div className="text-xs font-sports uppercase tracking-widest text-halo-navy/60 mb-5">
                  By {lead.author} · {formatDate(lead.published)}
                </div>
                <p className="font-body text-base leading-relaxed text-halo-navy/85 drop-cap">
                  {lead.excerpt}
                </p>
                <a
                  href={lead.url} target="_blank" rel="noreferrer"
                  data-testid="news-lead-readmore"
                  className="inline-flex items-center gap-1 mt-5 font-sports uppercase tracking-widest text-xs text-halo-red hover:text-halo-navy"
                >
                  Continue Reading <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={2.5} />
                </a>
              </article>
            )}

            <div className="lg:col-span-5 space-y-6">
              {sub.map((p) => (
                <article key={p.id} className="border-t border-halo-navy/30 pt-6 first:border-t-0 first:pt-0" data-testid={`news-article-${p.id?.slice(-6) || Math.random()}`}>
                  <h4 className="font-editorial text-xl text-halo-navy leading-tight mb-2 italic">
                    <a href={p.url} target="_blank" rel="noreferrer" className="hover:text-halo-red">{p.title}</a>
                  </h4>
                  <div className="text-[10px] font-sports uppercase tracking-widest text-halo-navy/50 mb-2">
                    {formatDate(p.published)}
                  </div>
                  <p className="font-body text-sm text-halo-navy/75 line-clamp-3">
                    {p.excerpt}
                  </p>
                </article>
              ))}
              <a
                href="https://substack.com/@angelsbaseballhiti" target="_blank" rel="noreferrer"
                data-testid="newspaper-subscribe-btn"
                className="block w-full bg-halo-navy text-halo-cream text-center py-4 font-sports uppercase tracking-wider text-sm hover:bg-halo-red transition-colors"
              >
                Subscribe to Newsletter →
              </a>
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
